import { logger } from '@/utils/logger/logger';
import { atom, getDefaultStore } from 'jotai';

export type DesktopCaptureActivity = 'transcribing' | 'dictating' | null;
export type LogoutConfirmationOutcome =
  | 'confirmed'
  | 'cancelled'
  | 'dismissed'
  | 'failedOpen';

type ActiveDesktopCaptureDetector = () => DesktopCaptureActivity;

export type DesktopLogoutConfirmationState =
  | {
      phase: 'confirm';
      purpose: 'logout';
      activity: Exclude<DesktopCaptureActivity, null>;
      resolve: (ok: boolean) => void;
    }
  | {
      phase: 'ending';
      purpose: 'logout' | 'closeApp';
      activity?: Exclude<DesktopCaptureActivity, null>;
    };

let activeDesktopCaptureDetector: { detect: ActiveDesktopCaptureDetector } | undefined;

export const desktopLogoutConfirmationAtom = atom<DesktopLogoutConfirmationState | null>(
  null
);

export function registerActiveDesktopCaptureDetector(
  detect: ActiveDesktopCaptureDetector
): () => void {
  const registration = { detect };
  activeDesktopCaptureDetector = registration;

  return () => {
    if (activeDesktopCaptureDetector === registration) {
      activeDesktopCaptureDetector = undefined;
    }
  };
}

export function getActiveDesktopCaptureActivity(): DesktopCaptureActivity {
  try {
    return activeDesktopCaptureDetector?.detect() ?? null;
  } catch (error) {
    // Fail open: a broken detector must never block logout.
    logger.warn('[desktopLogoutConfirmation] capture detector failed', {
      errorName: error instanceof Error ? error.name : typeof error,
    });
    return null;
  }
}

export function requestLogoutConfirmation(
  activity: Exclude<DesktopCaptureActivity, null>
): Promise<boolean> {
  const jotaiStore = getDefaultStore();
  if (jotaiStore.get(desktopLogoutConfirmationAtom)) {
    logger.info('[desktopLogoutConfirmation] confirmation resolved', {
      activity,
      outcome: 'dismissed',
    });
    return Promise.resolve(false);
  }

  return new Promise((resolve) => {
    jotaiStore.set(desktopLogoutConfirmationAtom, {
      phase: 'confirm',
      purpose: 'logout',
      activity,
      resolve,
    });
    logger.info('[desktopLogoutConfirmation] confirmation shown', { activity });
  });
}

export function openCaptureEndingDialog(purpose: 'logout' | 'closeApp'): void {
  const jotaiStore = getDefaultStore();
  if (jotaiStore.get(desktopLogoutConfirmationAtom)?.phase === 'confirm') {
    cancelPendingLogoutConfirmation();
  }
  if (jotaiStore.get(desktopLogoutConfirmationAtom) !== null) return;

  jotaiStore.set(desktopLogoutConfirmationAtom, {
    phase: 'ending',
    purpose,
  });
}

export function cancelPendingLogoutConfirmation(): void {
  const jotaiStore = getDefaultStore();
  const state = jotaiStore.get(desktopLogoutConfirmationAtom);
  if (state?.phase !== 'confirm') return;

  resolveLogoutConfirmation('dismissed');
}

export function resolveLogoutConfirmation(outcome: LogoutConfirmationOutcome): void {
  const jotaiStore = getDefaultStore();
  const state = jotaiStore.get(desktopLogoutConfirmationAtom);
  if (!state || state.phase !== 'confirm') return;

  const ok = outcome === 'confirmed' || outcome === 'failedOpen';
  if (ok) {
    jotaiStore.set(desktopLogoutConfirmationAtom, {
      phase: 'ending',
      purpose: 'logout',
    });
  } else {
    jotaiStore.set(desktopLogoutConfirmationAtom, null);
  }
  logger.info('[desktopLogoutConfirmation] confirmation resolved', {
    activity: state.activity,
    outcome,
  });
  state.resolve(ok);
}

export function closeLogoutConfirmation(): void {
  getDefaultStore().set(desktopLogoutConfirmationAtom, null);
}
